import { useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Trash2 } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  TableActionsMenu,
} from '@wundo/ui';
import {
  addCohortParticipant,
  cohortParticipantsKey,
  listCohortParticipants,
  removeCohortParticipant,
} from '@/lib/cohorts.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import type { TrackParticipant } from '@/types/cohorts.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { UserPicker } from '@/components/pickers/UserPicker.tsx';
import type { UserOption } from '@/components/pickers/userOption.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';

const columns: DataTableColumn<TrackParticipant>[] = [
  {
    key: 'user.id',
    header: 'Name',
    render: (row) => `${row.user?.first_name || ''} ${row.user?.last_name || ''}`.trim(),
  },
  { key: 'user.email', header: 'Email', render: (row) => row.user?.email || 'N/A' },
  { key: 'user.role', header: 'Role', render: (row) => row.user?.role || 'N/A' },
  {
    key: 'created_at',
    header: 'Enrolled At',
    render: (row) => formatDateTime(row.created_at),
  },
];

type ParticipantDialog =
  | { kind: 'add' }
  | { kind: 'remove'; participant: TrackParticipant };

export interface CohortParticipantsTabProps {
  cohortId: string;
  organisationId: string;
}

/** Who is in this cohort, and the two ways that list changes. */
export function CohortParticipantsTab({
  cohortId,
  organisationId,
}: CohortParticipantsTabProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<ParticipantDialog | null>(null);

  const participantsKey = cohortParticipantsKey(cohortId);

  const { data, isPending, isError } = useQuery({
    queryKey: [...participantsKey, page],
    queryFn: () => listCohortParticipants({ cohortId, page }),
  });

  const remove = useMutation({
    mutationFn: (participantId: string) => removeCohortParticipant(participantId),
    onSuccess: async () => {
      setDialog(null);
      await queryClient.invalidateQueries({ queryKey: participantsKey });
    },
  });

  const participant = dialog?.kind === 'remove' ? dialog.participant : null;

  return (
    <>
      <DataTable<TrackParticipant>
        label='Track Participants'
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        loading={isPending}
        error={isError ? 'Failed to load track participants' : undefined}
        emptyState={
          <EmptyState
            title='Nobody enrolled yet'
            description='Add the people who go through this cohort.'
          />
        }
        toolbar={
          <Button size='sm' onClick={() => setDialog({ kind: 'add' })}>
            Add Participant
          </Button>
        }
        pagination={{
          page: data?.meta.currentPage ?? page,
          pageSize: PAGE_SIZE,
          total: data?.meta.totalItems ?? 0,
        }}
        onPageChange={setPage}
        actions={(row) => (
          <TableActionsMenu
            label={`Actions for ${row.user?.email ?? 'participant'}`}
            items={[
              {
                label: 'View',
                icon: <Eye aria-hidden='true' />,
                onSelect: () =>
                  void router.navigate({ to: '/users/$id', params: { id: row.user_id } }),
              },
              {
                label: 'Delete',
                icon: <Trash2 aria-hidden='true' />,
                tone: 'danger',
                onSelect: () => setDialog({ kind: 'remove', participant: row }),
              },
            ]}
          />
        )}
      />

      <AddParticipantDialog
        open={dialog?.kind === 'add'}
        onClose={() => setDialog(null)}
        cohortId={cohortId}
        organisationId={organisationId}
      />

      <ConfirmDialog
        open={participant !== null}
        onOpenChange={(open) => !open && setDialog(null)}
        title='Remove participant?'
        description={
          participant
            ? `Are you sure you want to remove ${participant.user?.first_name} ${participant.user?.last_name} from this cohort?`
            : undefined
        }
        confirmLabel='Remove'
        cancelLabel='Cancel'
        variant='danger'
        onConfirm={async () => {
          if (participant) {
            await remove.mutateAsync(participant.id).catch(() => undefined);
          }
        }}
      />
    </>
  );
}

interface AddParticipantDialogProps {
  open: boolean;
  onClose: () => void;
  cohortId: string;
  /** Only people of the cohort's own organisation can join it. */
  organisationId: string;
}

function AddParticipantDialog({
  open,
  onClose,
  cohortId,
  organisationId,
}: AddParticipantDialogProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<UserOption | null>(null);
  const [error, setError] = useState('');

  const add = useMutation({
    mutationFn: (userId: string) => addCohortParticipant(cohortId, userId),
    onSuccess: async () => {
      close();
      await queryClient.invalidateQueries({
        queryKey: cohortParticipantsKey(cohortId),
      });
    },
    onError: (failure) =>
      setError(apiErrorMessage(failure, 'Failed to add participant to cohort')),
  });

  function close() {
    setSelected(null);
    setError('');
    onClose();
  }

  function submit() {
    if (!selected) {
      setError('Please select a user');
      return;
    }
    setError('');
    add.mutate(selected.id);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent size='md'>
        <DialogHeader>
          <DialogTitle>Add Participant to Cohort</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-3.5 px-5 py-4'>
          {error !== '' && (
            <p
              role='alert'
              className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
            >
              {error}
            </p>
          )}

          <Field label='Select User' required htmlFor='cohort-participant-user'>
            <UserPicker
              id='cohort-participant-user'
              placeholder='Search for a user...'
              value={selected}
              onChange={setSelected}
              organisationId={organisationId}
              clearable
              disabled={add.isPending}
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant='quiet' disabled={add.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={add.isPending || selected === null}
            loading={add.isPending}
            onClick={submit}
          >
            {add.isPending ? 'Adding...' : 'Add Participant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
